/*
  EnquiryInbox — admin page showing website submissions that arrived at
  `POST /api/enquiries/inbox`, split into Pending / Failed / Imported /
  Duplicate / Rejected tabs. Managers can approve pending rows, retry
  failed ones, or reject them as spam.

  Also hosts the settings block:
    - Rotate the shared inbox token
    - Copy the webhook URL (for the Elementor Webhook plugin)
    - Adjust the reCAPTCHA minimum score
    - Toggle "hold for approval" vs auto-import
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Inbox, ArrowLeft, RefreshCw, Copy, Check, ShieldAlert, AlertCircle,
  ChevronRight, Trash2, Loader2, KeyRound, Sparkles, Globe, PlayCircle,
  FileCode, BookOpen,
} from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-900 border-amber-200",
  imported: "bg-emerald-50 text-emerald-800 border-emerald-200",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  duplicate: "bg-zinc-100 text-zinc-600 border-zinc-200",
  rejected: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function EnquiryInbox() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [config, setConfig] = useState(null);
  const [tokenPlain, setTokenPlain] = useState(null); // shown once after rotate
  const [copied, setCopied] = useState(false);
  const [showTestWebhook, setShowTestWebhook] = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(null); // "php-header" | "url-token" | "instructions" | null
  const [snippetText, setSnippetText] = useState("");
  const [snippetLang, setSnippetLang] = useState("text");
  const [snippetBusy, setSnippetBusy] = useState(false);
  const [health, setHealth] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.get("/enquiry-inbox/health");
      setHealth(r.data || null);
    } catch { /* health widget is a nice-to-have — never toast */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rInbox, rCfg] = await Promise.all([
        api.get("/enquiry-inbox", { params: { status: tab } }),
        api.get("/settings/enquiries/inbox"),
      ]);
      setRows(rInbox.data?.rows || []);
      setCounters(rInbox.data?.counters || {});
      setConfig(rCfg.data || null);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
    // Refresh health alongside every list refresh so the widget
    // stays honest without needing a second manual button.
    loadHealth();
  }, [tab, loadHealth]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll the health endpoint every 2 minutes so a manager
  // leaving the tab open notices when WordPress goes silent
  // without having to hit Refresh.
  useEffect(() => {
    const id = setInterval(loadHealth, 120_000);
    return () => clearInterval(id);
  }, [loadHealth]);

  async function promote(id) {
    setBusyId(id);
    try {
      const r = await api.post(`/enquiry-inbox/${id}/promote`);
      const status = r.data?.status;
      if (status === "imported") toast.success("Imported into the enquiries register");
      else if (status === "failed") toast.error("Import failed", { description: r.data.failure_reason });
      load();
    } catch (e) {
      toast.error("Promote failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  }

  async function reject(id) {
    setBusyId(id);
    try {
      await api.post(`/enquiry-inbox/${id}/reject`, { reason: "spam" });
      toast.success("Marked as spam");
      load();
    } catch (e) {
      toast.error("Reject failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusyId(null); }
  }

  async function rotateToken() {
    if (!confirm("Rotate the inbox token? Your existing WordPress webhook URL will stop working until you paste the new token.")) return;
    try {
      const r = await api.post("/settings/enquiries/inbox/rotate-token");
      setTokenPlain(r.data.token);
      toast.success("New token generated");
      load();
    } catch (e) {
      toast.error("Rotate failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function openSnippet(variant) {
    setSnippetBusy(true);
    setSnippetOpen(variant);
    try {
      const r = await api.get(
        `/settings/enquiries/inbox/wordpress-snippet?variant=${variant}`,
      );
      setSnippetText(r.data.snippet || "");
      setSnippetLang(r.data.language || "text");
    } catch (e) {
      toast.error("Snippet fetch failed", {
        description: e?.response?.data?.detail || e.message,
      });
      setSnippetOpen(null);
    } finally { setSnippetBusy(false); }
  }

  async function saveConfig(next) {
    try {
      const r = await api.put("/settings/enquiries/inbox", next);
      setConfig(r.data);
      toast.success("Inbox settings saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const webhookUrl = useMemo(() => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/enquiries/inbox`;
  }, []);

  return (
    <div className="max-w-[1300px] mx-auto p-4 space-y-4" data-testid="enquiry-inbox-page">
      <div className="flex items-center gap-4 flex-wrap">
        <Link to="/enquiries" className="text-xs text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to enquiries
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <Inbox className="h-6 w-6" /> Website inbox
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Submissions from your WordPress / Elementor forms land here first, get deduped, and auto-promote into the Enquiries register.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="inbox-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {/* Health widget — traffic-light view of website deliverability. */}
      <InboxHealthWidget health={health} />

      {/* Settings block */}
      <Card className="p-4 border-zinc-200 space-y-3" data-testid="inbox-settings-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-zinc-700" />
            <span className="font-medium text-sm">Inbox token &amp; webhook</span>
          </div>
          <Button
            size="sm"
            onClick={rotateToken}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="inbox-rotate-btn"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            {config?.has_token ? "Rotate token" : "Generate token"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowTestWebhook(true)}
            className="text-emerald-800 border-emerald-300 hover:bg-emerald-50"
            data-testid="inbox-test-webhook-btn"
          >
            <PlayCircle className="h-3.5 w-3.5 mr-1" /> Test webhook
          </Button>
        </div>
        <div className="text-xs text-zinc-600">
          {config?.has_token ? (
            <>Active token ends in <code className="px-1 py-0.5 bg-zinc-100 rounded">…{config.token_last6}</code>. Rotate any time — the old token stops working immediately.</>
          ) : (
            <>No token yet. Generate one to start accepting website submissions.</>
          )}
        </div>
        {tokenPlain && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs space-y-2" data-testid="inbox-token-panel">
            <div className="font-semibold text-amber-900 flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5" /> Save this token now — we won&apos;t show it again.
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2 bg-white border border-amber-200 rounded font-mono-num break-all">{tokenPlain}</code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(tokenPlain);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                data-testid="inbox-token-copy"
              >
                {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> Copied</> : <><Copy className="h-3.5 w-3.5 mr-1" /> Copy</>}
              </Button>
            </div>
          </div>
        )}
        <div className="text-xs">
          <div className="text-zinc-500 uppercase tracking-wider mb-1">Webhook URL (paste into Elementor)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 p-2 bg-zinc-50 border border-zinc-200 rounded font-mono-num text-2xs break-all">{webhookUrl}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copied"); }}
              data-testid="inbox-webhook-copy"
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
        </div>
        {config?.recaptcha_enabled && (
          <div className="text-xs">
            <div className="text-zinc-500 uppercase tracking-wider mb-1">reCAPTCHA site key (for Elementor form)</div>
            <code className="p-2 bg-zinc-50 border border-zinc-200 rounded font-mono-num text-2xs break-all inline-block max-w-full">{config?.recaptcha_site_key}</code>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Minimum reCAPTCHA score (0-1)</Label>
            <Input
              type="number"
              min={0} max={1} step="0.1"
              defaultValue={config?.min_score ?? 0.5}
              onBlur={(e) => saveConfig({ min_score: parseFloat(e.target.value) })}
              className="mt-1 max-w-[120px]"
              data-testid="inbox-min-score"
            />
            <p className="text-2xs text-zinc-500 mt-0.5">Below this score, submissions are marked as failed spam and not auto-imported.</p>
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Approval mode</Label>
            <div className="mt-1">
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={!!config?.hold_for_approval}
                  onChange={(e) => saveConfig({ hold_for_approval: e.target.checked })}
                  data-testid="inbox-hold-for-approval"
                />
                Hold every submission for manual approval (default: auto-import if score ≥ min)
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Elementor + WordPress connection kit */}
      <Card className="p-4 border-zinc-200 space-y-3" data-testid="inbox-wordpress-kit">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-zinc-700" />
          <span className="font-medium text-sm">WordPress &amp; Elementor connection kit</span>
        </div>
        <p className="text-xs text-zinc-600">
          Copy the snippet that matches how you can edit your site.
          Both variants send to the same webhook and are equally
          valid — the PHP snippet is safer because your token never
          appears in a URL.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openSnippet("url-token")}
            data-testid="inbox-snippet-url-token"
          >
            <Globe className="h-3.5 w-3.5 mr-1" />
            Elementor Webhook URL (no PHP)
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openSnippet("php-header")}
            className="text-indigo-800 border-indigo-300 hover:bg-indigo-50"
            data-testid="inbox-snippet-php-header"
          >
            <FileCode className="h-3.5 w-3.5 mr-1" />
            functions.php header snippet
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openSnippet("instructions")}
            data-testid="inbox-snippet-instructions"
          >
            <BookOpen className="h-3.5 w-3.5 mr-1" />
            Field-name cheat sheet
          </Button>
        </div>
      </Card>

      {/* Elementor instructions */}
      <details className="text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md p-3" data-testid="inbox-elementor-help">
        <summary className="cursor-pointer font-medium flex items-center gap-1.5">
          <Globe className="h-3.5 w-3.5" /> Step-by-step: connect an Elementor Pro form
        </summary>
        <ol className="list-decimal list-inside space-y-1.5 mt-2 pl-1">
          <li>In this app, open <b>Settings → Enquiries → Inbox token</b> and click <b>Generate token</b>. Copy the token — it&apos;s shown only once.</li>
          <li>(Optional) In WordPress, install reCAPTCHA v3 for Elementor Pro under <b>Elementor → Settings → Integrations → reCAPTCHA v3</b>. Paste the same Site key + Secret you configured above.</li>
          <li>Edit the form widget. In each field&apos;s Advanced tab, set the ID to one of: <code>name</code>, <code>email</code>, <code>mobile</code>, <code>message</code>, <code>service_of_interest</code>, <code>preferred_contact_method</code>, <code>preferred_contact_time</code>, <code>privacy_acknowledged</code> (checkbox), <code>marketing_consent</code> (checkbox).</li>
          <li>Add hidden fields with the ID <code>utm_source</code>, <code>utm_medium</code>, <code>utm_campaign</code>, <code>page_url</code>, <code>referrer_url</code> and use Elementor&apos;s dynamic tags to populate them from the URL query string and referrer.</li>
          <li>Under <b>Actions After Submit</b> add the <b>Webhook</b> action. Use one of the two ready-made snippets above — the Webhook URL variant is fastest to try; the PHP header variant is safer for production.</li>
          <li>Enable Elementor&apos;s <b>Advanced Data</b> toggle on the Webhook action so UTM + form metadata are included.</li>
          <li>Publish, submit a test entry, and confirm it appears here as <b>Imported</b> within a few seconds.</li>
        </ol>
      </details>

      {/* Status tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="inbox-tabs">
          <TabsTrigger value="pending" data-testid="inbox-tab-pending">
            Pending <TabBadge n={counters.pending || 0} tone="amber" />
          </TabsTrigger>
          <TabsTrigger value="failed" data-testid="inbox-tab-failed">
            Failed <TabBadge n={counters.failed || 0} tone="rose" />
          </TabsTrigger>
          <TabsTrigger value="imported" data-testid="inbox-tab-imported">
            Imported (24h) <TabBadge n={counters.imported_24h || 0} tone="emerald" />
          </TabsTrigger>
          <TabsTrigger value="duplicate" data-testid="inbox-tab-duplicate">
            Duplicate (24h) <TabBadge n={counters.duplicate_24h || 0} tone="zinc" />
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="inbox-tab-rejected">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="pt-4">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500" data-testid="inbox-empty">
              Nothing here.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
              <ul className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100 max-h-[560px] overflow-auto">
                {rows.map((r) => (
                  <li
                    key={r.id}
                    onClick={() => setSelected(r)}
                    className={`p-3 cursor-pointer hover:bg-zinc-50 ${selected?.id === r.id ? "bg-zinc-50" : ""}`}
                    data-testid={`inbox-row-${r.id}`}
                  >
                    <div className="flex items-center gap-2 justify-between">
                      <div className="text-sm font-medium text-zinc-900 truncate">
                        {r.raw_payload?.name || r.raw_payload?.email || "Anonymous"}
                      </div>
                      <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_STYLES[r.status] || ""}`}>
                        {r.status}
                      </span>
                    </div>
                    <div className="text-2xs text-zinc-500 mt-0.5">
                      {r.raw_payload?.email || "—"} · {fmt(r.received_at)}
                    </div>
                    <div className="text-xs text-zinc-700 mt-1 line-clamp-2">
                      {r.raw_payload?.message || <em className="text-zinc-400">(no message)</em>}
                    </div>
                    {r.failure_reason && (
                      <div className="text-2xs text-rose-700 mt-1 line-clamp-1 flex items-start gap-1">
                        <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {r.failure_reason}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {selected ? (
                <InboxRowDetail
                  row={selected}
                  busy={busyId === selected.id}
                  onPromote={() => promote(selected.id)}
                  onReject={() => reject(selected.id)}
                />
              ) : (
                <div className="p-6 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
                  <ChevronRight className="h-5 w-5 mx-auto mb-1" /> Pick a submission to see full details.
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {showTestWebhook && (
        <TestWebhookDialog
          onClose={() => setShowTestWebhook(false)}
          onSubmitted={() => { load(); }}
        />
      )}

      <Dialog open={!!snippetOpen} onOpenChange={(v) => !v && setSnippetOpen(null)}>
        <DialogContent
          className="max-w-2xl"
          data-testid="inbox-snippet-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {snippetOpen === "php-header" && "functions.php header snippet"}
              {snippetOpen === "url-token" && "Elementor Webhook URL (URL-token variant)"}
              {snippetOpen === "instructions" && "Elementor field-name cheat sheet"}
            </DialogTitle>
            <DialogDescription>
              {snippetOpen === "php-header" && (
                <>Paste this into your child theme&apos;s <code>functions.php</code> (or a Code-Snippets plugin), then replace <code>YOUR_INBOX_TOKEN_HERE</code> with the token you copied from Settings.</>
              )}
              {snippetOpen === "url-token" && (
                <>Fastest way to try the integration. Copy this URL and paste it directly into Elementor&apos;s Webhook action, then replace the placeholder with the token.</>
              )}
              {snippetOpen === "instructions" && (
                <>Set each form field&apos;s <b>Advanced → CSS ID</b> to the values on the left; the app will match them automatically.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {snippetBusy ? (
            <div className="p-6 text-center text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
            </div>
          ) : (
            <pre
              className="text-2xs font-mono-num p-3 bg-zinc-950 text-zinc-100 rounded-md max-h-[420px] overflow-auto whitespace-pre-wrap break-words"
              data-testid="inbox-snippet-content"
              data-lang={snippetLang}
            >{snippetText}</pre>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(snippetText);
                toast.success("Snippet copied to clipboard");
              }}
              data-testid="inbox-snippet-copy"
              disabled={snippetBusy || !snippetText}
            >
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy to clipboard
            </Button>
            <Button variant="ghost" onClick={() => setSnippetOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function TestWebhookDialog({ onClose, onSubmitted }) {
  const [form, setForm] = useState({
    name: "Test User", email: "testwebhook@example.com", mobile: "0412345678",
    subject: "Test enquiry from webhook",
    message: "Hi — I would like to book an initial consultation for lower back pain.",
    service_of_interest: "Exercise Physiology",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function send() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/settings/enquiries/inbox/test", form);
      setResult(r.data);
      const s = r.data?.status;
      if (s === "imported") toast.success("Imported into enquiries — check the Imported tab");
      else if (s === "failed") toast.error("Test failed", { description: r.data.failure_reason });
      else if (s === "duplicate") toast.info("Marked as duplicate (already seen in the last 24h)");
      // Don't auto-close: leave the result panel visible so the operator
      // can copy the enquiry link. `onSubmitted` just triggers a
      // background refresh of the list beneath.
      onSubmitted?.();
    } catch (e) {
      toast.error("Test failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="test-webhook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4 text-emerald-700" /> Test the inbox pipeline
          </DialogTitle>
          <DialogDescription>
            Dry-runs a payload through the same code path Elementor hits.
            reCAPTCHA is bypassed for this call (you&apos;re signed in). The result
            appears in the Imported / Duplicate / Failed tabs as
            <code className="mx-1 px-1 rounded bg-zinc-100">source: test-webhook</code>
            so you can safely delete or reject it after.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Name</Label>
            <Input value={form.name} onChange={change("name")} className="mt-1" data-testid="test-webhook-name" />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Email</Label>
            <Input value={form.email} onChange={change("email")} className="mt-1" data-testid="test-webhook-email" />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Mobile</Label>
            <Input value={form.mobile} onChange={change("mobile")} className="mt-1" data-testid="test-webhook-mobile" />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Service</Label>
            <Input value={form.service_of_interest} onChange={change("service_of_interest")} className="mt-1" data-testid="test-webhook-service" />
          </div>
          <div className="col-span-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Subject</Label>
            <Input value={form.subject} onChange={change("subject")} className="mt-1" data-testid="test-webhook-subject" />
          </div>
          <div className="col-span-2">
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">Message</Label>
            <Textarea rows={3} value={form.message} onChange={change("message")} className="mt-1" data-testid="test-webhook-message" />
          </div>
        </div>

        {result && (
          <div className="rounded-md border p-3 text-xs space-y-1" data-testid="test-webhook-result">
            <div className="flex items-center gap-2">
              <span className="uppercase tracking-wider text-zinc-500">Result:</span>
              <span className={`px-1.5 py-0.5 rounded border text-2xs uppercase ${STATUS_STYLES[result.status] || ""}`}>{result.status}</span>
            </div>
            {result.enquiry_id && (
              <div>
                Imported as enquiry{" "}
                <Link to={`/enquiries?open=${result.enquiry_id}`} className="text-emerald-800 hover:underline font-mono-num">
                  {result.enquiry_id}
                </Link>
              </div>
            )}
            {result.failure_reason && (
              <div className="text-rose-700">{result.failure_reason}</div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={send}
            disabled={busy || !form.message?.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="test-webhook-send"
          >
            {busy
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Testing…</>
              : <><PlayCircle className="h-3.5 w-3.5 mr-1" /> Run test</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabBadge({ n, tone }) {
  if (!n) return null;
  const tones = {
    amber: "bg-amber-100 text-amber-900",
    rose: "bg-rose-100 text-rose-900",
    emerald: "bg-emerald-100 text-emerald-900",
    zinc: "bg-zinc-200 text-zinc-800",
  };
  return (
    <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-4 rounded-full px-1 text-2xs font-semibold ${tones[tone] || tones.zinc}`}>
      {n}
    </span>
  );
}

function InboxRowDetail({ row, onPromote, onReject, busy }) {
  const raw = row.raw_payload || {};
  const extracted = row.extracted || {};
  const isPromotable = ["pending", "failed", "duplicate"].includes(row.status);
  return (
    <Card className="p-4 space-y-3 border-zinc-200" data-testid="inbox-row-detail">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-950">{raw.name || raw.email || "Anonymous"}</div>
          <div className="text-2xs text-zinc-500">{fmt(row.received_at)} · {row.source || "elementor"}</div>
        </div>
        <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_STYLES[row.status] || ""}`}>
          {row.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <Kv k="Email" v={raw.email} />
        <Kv k="Mobile" v={raw.mobile} />
        <Kv k="Subject" v={raw.subject} span={2} />
        <Kv k="Service" v={raw.service_of_interest} span={2} />
      </div>

      <div>
        <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-1">Message</div>
        <div className="p-2 bg-zinc-50 border border-zinc-200 rounded text-sm text-zinc-800 whitespace-pre-wrap">{raw.message || <em className="text-zinc-400">(empty)</em>}</div>
      </div>

      {row.enquiry_id && (
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
          Imported into enquiry{" "}
          <Link to={`/enquiries?open=${row.enquiry_id}`} className="font-medium text-emerald-800 hover:underline">
            {row.enquiry_id}
          </Link>
        </div>
      )}

      {row.failure_reason && (
        <div className="text-xs bg-rose-50 border border-rose-200 rounded p-2 text-rose-900 flex items-start gap-1">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> {row.failure_reason}
        </div>
      )}

      {row.recaptcha && (
        <div className="text-2xs text-zinc-500">
          reCAPTCHA score: <b>{row.recaptcha.score ?? 0}</b> · host: {row.recaptcha.hostname || "—"}
        </div>
      )}

      {Object.keys(extracted).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-zinc-500 uppercase tracking-wider">AI extracted</summary>
          <pre className="mt-1 p-2 bg-indigo-50 border border-indigo-200 rounded whitespace-pre-wrap text-2xs">{JSON.stringify(extracted, null, 2)}</pre>
        </details>
      )}

      {row.raw_body_debug && Object.keys(row.raw_body_debug).length > 0 && (
        <details className="text-xs" data-testid="inbox-row-raw-body">
          <summary className="cursor-pointer text-zinc-500 uppercase tracking-wider">
            Raw payload (debug) — what your WordPress form actually sent
          </summary>
          <div className="mt-1 space-y-2">
            <div className="text-2xs text-zinc-500">
              Use this to check which field IDs your form is sending. If a field you expected shows here under a name we don&apos;t recognise, tell the developer and we&apos;ll add it to the alias list.
            </div>
            <pre className="p-2 bg-amber-50 border border-amber-200 rounded whitespace-pre-wrap text-2xs max-h-[280px] overflow-auto">{JSON.stringify(row.raw_body_debug, null, 2)}</pre>
          </div>
        </details>
      )}

      <div className="flex items-center gap-2 pt-1">
        {isPromotable && (
          <Button
            onClick={onPromote}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="inbox-promote-btn"
          >
            {busy
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Working…</>
              : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Import to enquiries</>}
          </Button>
        )}
        {row.status !== "rejected" && (
          <Button
            variant="outline"
            onClick={onReject}
            disabled={busy}
            className="text-rose-700 border-rose-300 hover:bg-rose-50"
            data-testid="inbox-reject-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Reject as spam
          </Button>
        )}
      </div>
    </Card>
  );
}

function Kv({ k, v, span = 1 }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-2xs uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="text-zinc-800 break-words">{v || <span className="text-zinc-400">—</span>}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// InboxHealthWidget — traffic-light view of website deliverability.
// One glance answers: "Is WordPress still sending?"
//
//   healthy → green  · at least one recent success, no red flags
//   quiet   → grey   · never received one — probably not wired up yet
//   stale   → amber  · no submissions in 24h, last success > 3d ago
//   failing → red    · 3+ failures in the last 24h (or > 50% error rate)
//
// The widget polls /enquiry-inbox/health every 2 min so an idle admin
// still notices when the site goes silent overnight.
// ---------------------------------------------------------------------------
function InboxHealthWidget({ health }) {
  // Placeholder skeleton while the first fetch is in flight — keeps
  // the layout stable so the settings card doesn't jump on load.
  if (!health) {
    return (
      <div
        className="rounded-lg border border-zinc-200 bg-white p-3 flex items-center gap-3 text-sm text-zinc-500"
        data-testid="inbox-health-widget"
        data-state="loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking website deliverability…
      </div>
    );
  }

  const cfg = {
    healthy: { pill: "Healthy",        wrap: "border-emerald-200 bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-900" },
    quiet:   { pill: "Awaiting first submission", wrap: "border-zinc-200 bg-zinc-50",       dot: "bg-zinc-400",    text: "text-zinc-800" },
    stale:   { pill: "No traffic",     wrap: "border-amber-200 bg-amber-50",     dot: "bg-amber-500",   text: "text-amber-900" },
    failing: { pill: "Delivery failing", wrap: "border-red-200 bg-red-50",         dot: "bg-red-500",     text: "text-red-900" },
  }[health.status] || {
    pill: health.status, wrap: "border-zinc-200 bg-zinc-50", dot: "bg-zinc-400", text: "text-zinc-800",
  };

  return (
    <div
      className={`rounded-lg border ${cfg.wrap} p-3`}
      data-testid="inbox-health-widget"
      data-state={health.status}
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block h-2 w-2 rounded-full ${cfg.dot} ${health.status === "healthy" ? "animate-pulse" : ""}`} />
          <span className={`text-sm font-semibold ${cfg.text}`} data-testid="inbox-health-status-pill">
            {cfg.pill}
          </span>
        </div>
        <div className="flex-1 min-w-[240px] text-xs text-zinc-700 space-x-3 font-mono-num" data-testid="inbox-health-metrics">
          <span>
            Last successful:{" "}
            <span className="font-medium text-zinc-900">
              {inboxRelative(health.last_success_at) || "never"}
            </span>
          </span>
          <span aria-hidden="true" className="text-zinc-400">·</span>
          <span>
            <span className="font-medium text-zinc-900">{health.imported_7d}</span> submissions this week
          </span>
          <span aria-hidden="true" className="text-zinc-400">·</span>
          <span>
            <span className={"font-medium " + (health.failed_24h > 0 ? "text-red-700" : "text-zinc-900")}>
              {health.failed_24h}
            </span>{" "}
            failed <span className="text-zinc-500">(24h)</span>
          </span>
          {health.duplicate_24h > 0 && (
            <>
              <span aria-hidden="true" className="text-zinc-400">·</span>
              <span>
                <span className="font-medium text-zinc-900">{health.duplicate_24h}</span> deduped
              </span>
            </>
          )}
        </div>
      </div>
      {health.hint && (
        <div className={`mt-2 text-xs ${cfg.text}`} data-testid="inbox-health-hint">
          {health.hint}
        </div>
      )}
    </div>
  );
}

/** Compact "3 minutes ago" formatter — kept private to the widget so
 *  it always matches the copy above ("Last successful: 3 minutes ago"). */
function inboxRelative(ts) {
  if (!ts) return "";
  try {
    const then = new Date(ts).getTime();
    const diff = Math.max(0, Date.now() - then);
    if (diff < 60_000) return "just now";
    if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
    if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} hr ago`;
    return `${Math.round(diff / 86_400_000)} day${diff >= 172_800_000 ? "s" : ""} ago`;
  } catch { return ""; }
}

